export interface FunnelState {
  id: string;
  label: string;
  terminal: boolean;
}

export interface FunnelCount {
  state: string;
  casos: number;
}

export interface StateFunnelProps {
  /** El set declarado por el tenant. **Define el orden.** */
  states: FunnelState[];
  /** Conteos por id de estado, como vienen del API. */
  counts: FunnelCount[];
}

/**
 * Embudo de operaciones, en el orden que declaró el tenant (ADR-023).
 *
 * El orden sale de la POSICIÓN en `states`, no del alfabeto ni del conteo: es el
 * proceso del negocio y el único que lo conoce es el tenant. Si este componente
 * ordenara solo, la decisión de dejar que cada cliente nombre sus estados no
 * serviría de nada.
 *
 * Un estado que el tenant borró de su config se muestra igual, **al final y con
 * su id**: los casos históricos existen y ocultarlos sería mentir sobre el
 * volumen. Va marcado con `data-huerfano` para que se lea distinto de uno
 * vigente.
 *
 * Tokens semánticos, nunca primitivos (L29): Tailwind **descarta en silencio**
 * una clase que no conoce — no da error, no da estilo, y el componente queda sin
 * fondo sin que nadie se entere hasta mirar la pantalla.
 */
export function StateFunnel({ states, counts }: StateFunnelProps) {
  const porEstado = new Map(counts.map((c) => [c.state, c.casos]));

  const declarados = states.map((estado) => ({
    id: estado.id,
    label: estado.label,
    casos: porEstado.get(estado.id) ?? 0,
    huerfano: false,
  }));

  // Los que ya no están declarados van al final, con su id como etiqueta.
  const huerfanos = counts
    .filter((c) => !states.some((e) => e.id === c.state))
    .map((c) => ({ id: c.state, label: c.state, casos: c.casos, huerfano: true }));

  const filas = [...declarados, ...huerfanos];
  const total = filas.reduce((acc, f) => acc + f.casos, 0);

  // Un embudo con todas las barras en cero se lee como un error de carga, no
  // como "todavía no pasó nada".
  if (total === 0) return null;

  // Relativo al MÁXIMO y no al total: con un estado dominante, relativo al total
  // deja al resto en una línea de un píxel y el embudo deja de leerse.
  const maximo = Math.max(...filas.map((f) => f.casos), 1);

  return (
    <ol data-testid="state-funnel" className="flex flex-col gap-2">
      {filas.map((fila) => (
        <li
          key={fila.id}
          data-testid={`funnel-row-${fila.id}`}
          data-state={fila.id}
          {...(fila.huerfano ? { 'data-huerfano': 'true' } : {})}
          className="flex items-center gap-3"
        >
          <span
            className={`w-40 shrink-0 truncate text-sm ${
              fila.huerfano ? 'text-content-tertiary italic' : 'text-content-secondary'
            }`}
            title={fila.label}
          >
            {fila.label}
          </span>

          <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-sunken">
            <span
              data-testid="funnel-bar"
              className={`block h-full rounded-full ${
                fila.huerfano ? 'bg-content-tertiary' : 'bg-surface-brand-soft'
              }`}
              style={{ width: `${(fila.casos / maximo) * 100}%` }}
            />
          </span>

          <span className="w-12 shrink-0 text-right font-mono text-sm tabular-nums text-content-primary">
            {fila.casos}
          </span>
        </li>
      ))}
    </ol>
  );
}
